/**
 * SearchableSelect — a custom popover "select" with a built-in search box.
 *
 * Why not Radix Select? Radix Select moves DOM focus to whatever item the mouse
 * hovers (and re-focuses items on every collection change), which is
 * fundamentally incompatible with an always-focused in-content search box — the
 * caret kept getting yanked out mid-type. This mirrors the working clients-page
 * filter dropdown (a hand-rolled popover) instead, so search "just works".
 *
 * It deliberately preserves the test-facing contract of the Radix version:
 *   - the trigger carries the caller's `data-testid` and renders the selected
 *     option's content as its text (so `toHaveTextContent(...)` still passes),
 *   - options are `role="option"` with `aria-disabled` + optional `title`,
 *   - group labels render as plain text.
 *
 * The panel is portaled to <body> so it escapes the settings panel's
 * `overflow-hidden`, and is positioned to the LEFT of the trigger (with the
 * app-standard rotate-45 beak pointing back at it), matching the prior look.
 */

import { useState, useRef, useEffect, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MagnifyingGlass, CaretDown, Check } from "@phosphor-icons/react";
import { cn } from "@provenleap/ui";

export interface SearchableSelectOption {
  value: string;
  /** Row content (logo + label, suffixes, …). Also shown in the trigger when selected. */
  content: React.ReactNode;
  /** Lowercased-on-compare text used for filtering (label + any keywords). */
  searchText: string;
  disabled?: boolean;
  title?: string;
}

export interface SearchableSelectGroup {
  key: string;
  label: string;
  options: SearchableSelectOption[];
}

interface SearchableSelectProps {
  value?: string;
  onChange: (value: string) => void;
  groups: SearchableSelectGroup[];
  placeholder?: string;
  searchPlaceholder?: string;
  /** Width gutter (px) between the panel's right edge and the trigger's left edge. */
  gap?: number;
  testId?: string;
  searchTestId?: string;
  triggerClassName?: string;
  emptyText?: string;
  /** Hide the search box (for short option lists where filtering is noise). */
  hideSearch?: boolean;
  /** Accessible label for the trigger button. */
  ariaLabel?: string;
}

const GAP_DEFAULT = 8;

export function SearchableSelect({
  value,
  onChange,
  groups,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  gap = GAP_DEFAULT,
  testId,
  searchTestId,
  triggerClassName,
  emptyText = "No results found",
  hideSearch = false,
  ariaLabel,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pos, setPos] = useState<{ top: number; right: number; minWidth: number; maxHeight: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const selected = groups.flatMap((g) => g.options).find((o) => o.value === value);

  // Filter: a group-label hit surfaces the whole group; otherwise filter items.
  const q = search.trim().toLowerCase();
  const filteredGroups = q
    ? groups
        .map((g) =>
          g.label.toLowerCase().includes(q)
            ? g
            : { ...g, options: g.options.filter((o) => o.searchText.toLowerCase().includes(q)) },
        )
        .filter((g) => g.options.length > 0)
    : groups;
  const isEmpty = filteredGroups.length === 0;

  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Anchor the panel's RIGHT edge just left of the trigger so it grows leftward
    // (content-sized) and never overlaps the trigger.
    const MARGIN = 8; // keep the panel off the viewport edges
    const HARD_MAX = 384; // matches the design cap (max-h-96)
    const spaceBelow = window.innerHeight - r.top - MARGIN;
    let top = r.top;
    let maxHeight = Math.min(HARD_MAX, spaceBelow);
    // If there isn't enough room below, lift the panel up so it stays on screen
    // (clamped to the top margin) and grant it the larger available height.
    if (maxHeight < 200) {
      const desired = Math.min(HARD_MAX, window.innerHeight - 2 * MARGIN);
      top = Math.max(MARGIN, window.innerHeight - MARGIN - desired);
      maxHeight = Math.min(desired, window.innerHeight - top - MARGIN);
    }
    setPos({ top, right: Math.max(0, window.innerWidth - r.left + gap), minWidth: r.width, maxHeight });
  }, [gap]);

  // Position on open, and keep it pinned while scrolling/resizing.
  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, reposition]);

  // Click-outside (trigger + panel are separate DOM trees via the portal) + Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Reset the query whenever the dropdown closes.
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const pick = (opt: SearchableSelectOption) => {
    if (opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-testid={testId}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          // touch-target-sm: opt out of the global button min-height:44px so the
          // trigger honours its own height (h-9 for compact field selects, h-11 for
          // the big Integration/Metric pickers) — keeps select height == input height.
          "touch-target-sm flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer focus:outline-none focus-visible:outline-none [&>span]:line-clamp-1",
          triggerClassName,
        )}
      >
        <span className="flex min-w-0 items-center truncate">
          {selected ? selected.content : <span className="text-muted-foreground">{placeholder}</span>}
        </span>
        <CaretDown size={16} className="shrink-0 opacity-50" />
      </button>

      {pos &&
        createPortal(
          <AnimatePresence>
          {open && (
          <motion.div
            ref={panelRef}
            role="listbox"
            className="fixed z-[60] flex flex-col rounded-xl border-[0.5px] border-input bg-popover text-popover-foreground shadow-md"
            style={{ top: pos.top, right: pos.right, minWidth: pos.minWidth, maxHeight: pos.maxHeight, transformOrigin: "top right" }}
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.14, ease: [0.4, 0, 0.2, 1] }}
          >
            {/* Beak — rotate-45 square on the right edge, pointing at the trigger. */}
            <div
              className="absolute -right-[5px] top-5 w-2.5 h-2.5 bg-popover rotate-45 z-10"
              style={{ borderRight: "0.5px solid var(--hairline)", borderTop: "0.5px solid var(--hairline)" }}
            />

            {/* Pinned search — identical design to the clients-page filter dropdown.
                Hidden for short option lists (hideSearch). */}
            {!hideSearch && (
            <div className="shrink-0 px-2 pt-2 pb-0.5">
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  autoFocus
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  data-testid={searchTestId}
                  className="w-full pl-8 pr-3 py-2 text-[13px] text-foreground bg-transparent placeholder:text-muted-foreground placeholder:font-normal font-medium rounded-lg border-[0.5px] border-input focus:outline-none focus-visible:outline-none"
                  style={{ outline: "none", boxShadow: "none" }}
                />
              </div>
            </div>
            )}

            {/* Scrollable options */}
            <div className="min-h-0 flex-1 overflow-y-auto rounded-xl p-2">
              {isEmpty && (
                <p className="px-3 py-3 text-center text-[13px] text-muted-foreground">{emptyText}</p>
              )}
              {filteredGroups.map((group) => (
                <div key={group.key}>
                  {group.label && <div className="py-1.5 pl-2 pr-2 text-sm font-semibold">{group.label}</div>}
                  {group.options.map((opt) => {
                    const isSelected = opt.value === value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        aria-disabled={opt.disabled || undefined}
                        title={opt.title}
                        onClick={() => pick(opt)}
                        className={cn(
                          "relative flex w-full select-none items-center rounded-lg py-1.5 pl-4 pr-8 text-sm text-left outline-none border-[0.9px] border-transparent",
                          opt.disabled
                            ? "pointer-events-none opacity-50 cursor-not-allowed"
                            : "cursor-pointer hover:bg-muted/20 hover:border-input hover:shadow-xs",
                        )}
                      >
                        <span className="flex min-w-0 flex-1 items-center truncate">{opt.content}</span>
                        {isSelected && (
                          <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                            <Check size={16} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </motion.div>
          )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
}
